import { createContext, useContext, useMemo } from 'react';
import { useAuth as useClerkAuth, useUser } from '@clerk/clerk-react';

const AuthContext = createContext(null);

/**
 * Thin adapter over Clerk — isAuthenticated mirrors Clerk isSignedIn only.
 */
export const AuthProvider = ({ children }) => {
  const { isSignedIn, isLoaded, signOut } = useClerkAuth();
  const { user } = useUser();

  const value = useMemo(
    () => ({
      /** True when Clerk session is active (not a separate client-side flag). */
      isAuthenticated: Boolean(isSignedIn),
      isLoaded: Boolean(isLoaded),
      isSignedIn: Boolean(isSignedIn),
      user: user
        ? {
            id: user.id,
            email: user.primaryEmailAddress?.emailAddress ?? '',
            firstName: user.firstName ?? '',
            lastName: user.lastName ?? '',
            fullName: user.fullName ?? '',
            imageUrl: user.imageUrl ?? '',
          }
        : null,
      logout: () => signOut(),
    }),
    [isSignedIn, isLoaded, user, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider (inside ClerkProvider)');
  }
  return ctx;
};
